// File: src/components/layouts/navigation/Breadcrumbs/Breadcrumbs.tsx
// Purpose: Layout or container component

import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Typography } from "../../../general/Typography";
import { ChevronRight, Home, MoveHorizontal } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  onClick?: (path: string) => void;
  as?: React.ElementType; // `<a>`, `<Link>`, or Next.js `Link`
  compact?: boolean; // ✅ Collapsible for mobile
}

/** 🔄 Smooth fade-in animation */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** ✅ Breadcrumb Wrapper */
const BreadcrumbsWrapper = styled.nav<{ compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  overflow-x: auto; /* ✅ Prevents breaking on small screens */
  white-space: nowrap;
  padding: ${({ theme }) => theme.spacing.xs};
  animation: ${fadeIn} 0.3s ease-in-out;

  ${({ compact, theme }) =>
    compact &&
    css`
      @media (max-width: ${theme.breakpoints.tablet}) {
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing.md};
      }
    `}
`;

/** ✅ Breadcrumb Item */
const BreadcrumbItemWrapper = styled.span`
  display: flex;
  align-items: center; /* ✅ Ensures vertical alignment */
  gap: ${({ theme }) => theme.spacing.xxs};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  animation: ${fadeIn} 0.3s ease-in-out;
`;

/** ✅ Separator (Chevron) */
const Separator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.spacing.xxs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

/** ✅ Compact Mode Toggle */
const CompactToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;

  padding: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.textPrimary};
  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  onClick,
  as: LinkComponent = "a",
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  /** ✅ Toggle compact mode */
  const toggleCompact = () => setExpanded((prev) => !prev);

  /** ✅ Decide which items to show (for compact mode) */
  const visibleItems =
    compact && !expanded
      ? [items[0], { label: "...", icon: null }, items[items.length - 1]]
      : items;

  return (
    <BreadcrumbsWrapper compact={compact}>
      {visibleItems.map((item, index) => (
        <React.Fragment key={index}>
          <LinkComponent
            href={item.path || "#"}
            onClick={() => item.path && onClick?.(item.path)}
            style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <BreadcrumbItemWrapper>
              {item.icon && <span>{item.icon}</span>}
              <Typography
                as="span"
                variant="caption" /* ✅ Correct variant from TypographyProps */
                weight="medium"
                colorScheme={index === visibleItems.length - 1 ? "textPrimary" : "textSecondary"} /* ✅ Last breadcrumb is highlighted */
              >
                {item.label}
              </Typography>
            </BreadcrumbItemWrapper>
          </LinkComponent>

          {/* ✅ Add separator unless it's the last item */}
          {index < visibleItems.length - 1 && (
            <Separator>
              <ChevronRight size={14} />
            </Separator>
          )}
        </React.Fragment>
      ))}

      {/* ✅ Toggle button for compact mode */}
      {compact && (
        <CompactToggle onClick={toggleCompact} title="Expand breadcrumbs">
          <MoveHorizontal size={14} />
        </CompactToggle>
      )}
    </BreadcrumbsWrapper>
  );
};